
import { Component, Emit, Mixins, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Route } from 'vue-router';

import BuyBoxFilter from '@/components/buybox/BuyBoxFilter.vue';
import BuyBoxRow from '@/components/buybox/BuyBoxRow.vue';
import {
	GetTitleOffersDocument,
	GetTitleOffersQuery,
	GetTitleOffersQueryVariables,
} from '@/components/buybox/graphql/queries/GetTitleOffers.query';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';

const OfferReporter = () => import('@/components/buybox/OfferReporter.vue');
const BuyBoxFastRow = () => import('@/components/buybox/BuyBoxFastRow.vue');
const BuyBoxNoOfferRow = () => import('@/components/buybox/BuyBoxNoOfferRow.vue');
const PromotionRow = () => import('@/components/buybox/PromotionRow.vue');
// APPLE_QUALTIY_TV_EXP
const AppleQualityTvBuyBox = () => import('@/components/experiments/QualityTvExp/components/AppleQualityTvBuyBox.vue');
// APPLE_QUALTIY_TV_EXP
import { ImpressionTrackingEvents } from '@/enums/events';

import { NarrativeObjectType, OfferLocation, trackClickOutGraphql } from '@/helpers/offer-helper';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { timestamp } from '@/helpers/date-helper';

import type { TitleOffers } from '@/interfaces/buybox';
import { OfferPresentationType } from '@/interfaces/titles';

import type { ObjectType, Offer as OfferGraphql, Package, Scoring } from '@/@types/graphql-types';
import { MonetizationType, PresentationType, SraEventAction } from '@/@types/graphql-types';
import { ClickoutContentType } from '@/interfaces/snowplow/clickout-context';
import { TitleDetailsNode } from '@/interfaces/title-details-graphql';
import type { SponsoredAdFragmentFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';

import { useApolloData } from '@/helpers/composables/useApolloData';
import { UsePromotion, allowedPromotionLanguages, usePromotion } from '@/helpers/composables/usePromotion';

// BUYBOX_EXP_4
import {
	buyboxExp4TrackClickout,
	buyboxExp4TrackFreeTrialClickout,
	buyboxExp4TrackNotifyMeClick,
} from '@/components/experiments/buybox-exp-4/tracking';
// BUYBOX_EXP_4

// BUYBOX_EXP_5
import {
	buyboxExp5TrackClickout,
	buyboxExp5TrackFreeTrialClickout,
	buyboxExp5TrackNotifyMeClick,
} from '@/components/experiments/buybox-exp-5/tracking';
// BUYBOX_EXP_5

import BackendTrackingMixin from '@/mixins/BackendTracking.vue';
import PromotionRowV2 from '@/components/buybox/PromotionRowV2.vue';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { FALLBACK_PROMOTION_OFFER } from '@/helpers/promotion-helper';

// APPLE_BACKDROP_EXP
import AppleBackdropExp from '@/components/experiments/AppleBackdropExp';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
import { AppleBackdropExpPropsType } from '@/components/experiments/AppleBackdropExp/types';
// APPLE_BACKDROP_EXP

// APPLE_QUALTIY_TV_EXP
import AppleQualityTvExp, { AppleQualityTvExpName } from '@/components/experiments/QualityTvExp';
// APPLE_QUALTIY_TV_EXP

const Constant = namespace('constant');
const Language = namespace('language');
const Routing = namespace('routing');
const User = namespace('user');
// APPLE_QUALTIY_TV_EXP
const Experiment = namespace('experiment');
// APPLE_QUALTIY_TV_EXP

@Component({
	name: 'BuyBox',
	components: {
		OfferReporter,
		BuyBoxFilter,
		ProviderIcon,
		BuyBoxRow,
		BuyBoxFastRow,
		BuyBoxNoOfferRow,
		PromotionRow,
		PromotionRowV2,
		AppleQualityTvBuyBox,
	},
	apollo: {
		offers: {
			query: GetTitleOffersDocument,
			variables(): GetTitleOffersQueryVariables {
				return this.queryVariables;
			},
			update(data: GetTitleOffersQuery) {
				if (data.node == null) return;

				if (
					data.node.__typename === 'Episode' ||
					data.node.__typename === 'Movie' ||
					data.node.__typename === 'Season' ||
					data.node.__typename === 'Show'
				) {
					const { buy, rent, fast, free, flatrate, offerCount, maxOfferUpdatedAt } = data.node;
					const result: TitleOffers = {
						stream: [],
						rent,
						buy,
						free,
						fast,
						cinema: [],
						offerCount,
						maxOfferUpdatedAt,
					};
					flatrate.forEach(offer =>
						offer.monetizationType === MonetizationType.Cinema
							? result.cinema.push(offer)
							: result.stream.push(offer)
					);
					return result;
				}
			},
			watchLoading(isLoading) {
				if (!process.server) {
					!this.buyboxLoaded && this.onBuyboxLoadingChange(isLoading);
				}
			},
		},
	},
	setup({ title }) {
		const { inPromotionCatalogue, promotionOffer, promotionCatalogue } = usePromotion({
			title,
			offers: () => {
				const { free = [], stream } = useApolloData<TitleOffers>('offers')();
				return [...free, ...stream];
			},
		});

		const { isArbitrating } = useArbitrage();

		return { inPromotionCatalogue, promotionOffer, promotionCatalogue, isArbitrating };
	},
})
export default class BuyBox extends Mixins(BackendTrackingMixin) {
	buyboxLoaded = false;
	offers: TitleOffers = {
		stream: [],
		rent: [],
		buy: [],
		free: [],
		fast: [],
		cinema: [],
		offerCount: 0,
		maxOfferUpdatedAt: '',
	};

	// usePromotion composable exports
	declare inPromotionCatalogue: UsePromotion['inPromotionCatalogue'];
	declare promotionOffer: UsePromotion['promotionOffer'];
	declare promotionCatalogue: UsePromotion['promotionCatalogue'];
	declare isArbitrating: boolean;

	// TODO: remove all title* props across <BuyBox> usages
	@Prop({ required: true }) titleId: string;
	@Prop({ required: true }) titleObjectType: NarrativeObjectType; // base title objectType
	@Prop({ required: true }) titleObjectId: number; // base title id
	@Prop({ default: '' }) titleName: string;
	@Prop({ required: false }) title: TitleDetailsNode;
	// You need to pass this prop when title is Season/Episode, because BuyBox component has functionality to set
	// title into Watchlist, but this mutation available only for Movie/Show. So basically when you set Season/Episode
	// to Watchlist we set whole Show to watchlist.
	@Prop({ default: null }) showDetails: {
		id: string;
		objectId: number;
		objectType: ObjectType;
		title: string;
	};
	@Prop({ default: false }) isTitleInWatchlist: boolean;
	@Prop({ default: () => ({}) }) titleScoring: Scoring;
	@Prop({ default: () => null }) seasonNumber: number | null;
	@Prop({ default: () => null }) episodeNumber: number | null;
	@Prop({ default: '' }) originalReleaseYear: number;
	@Prop({ default: null }) sponsoredAd: SponsoredAdFragmentFragment;
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];

	// Show filter bar
	@Prop({ default: false, type: Boolean }) showFilter!: boolean;

	// display as inline
	@Prop(Boolean) inline: boolean;

	// no header 'WATCH NOW'
	@Prop({ default: false }) noHeader: boolean;

	@Prop() defaultPresentationType: OfferPresentationType;
	@Prop({ default: false }) isInModal: boolean;

	// START AVOD
	@Prop({ default: false }) isAVODActive: boolean;
	// END AVOD

	// APPLE_BACKDROP_EXP_3
	@Prop() appleBackdropExpProps: AppleBackdropExpPropsType;
	// APPLE_BACKDROP_EXP_3

	// BUYBOX_EXP_4
	@Prop() buyboxExp4ValidationPassed: boolean;
	// BUYBOX_EXP_4

	// BUYBOX_EXP_5
	@Prop() buyboxExp5ValidationPassed: boolean;
	// BUYBOX_EXP_5

	@Constant.Getter allProvidersById: Record<number, Package>;
	@Constant.Getter titleProviderCount: number;

	@Language.Getter declare language: string;
	@Language.Getter declare country: string;

	@Routing.State activeRoute: Route;

	@User.Getter buyboxPresentationType: OfferPresentationType;
	@User.State isPremium: boolean;
	@User.State jwId: string;
	@User.Getter isLoggedIn: () => boolean;

	// APPLE_QUALTIY_TV_EXP
	@Experiment.Getter activeVariantsWithControlGroup: Record<string, string>;
	// APPLE_QUALTIY_TV_EXP

	@Emit('onWatchlistToggle')
	onWatchlistToggle(createdAt: string | null) {
		// BUYBOX_EXP_4
		if (this.buyboxExp4ValidationPassed) {
			const contexts = [...this.additionalContexts];
			buyboxExp4TrackNotifyMeClick(contexts);
		}
		// BUYBOX_EXP_4

		// BUYBOX_EXP_5
		if (this.buyboxExp5ValidationPassed) {
			const contexts = [...this.additionalContexts];
			buyboxExp5TrackNotifyMeClick(contexts);
		}
		// BUYBOX_EXP_5

		return createdAt;
	}

	@Emit('buyboxLoading')
	onBuyboxLoadingChange(isLoading: boolean) {
		if (!isLoading) {
			this.buyboxLoaded = true;
		}

		return isLoading;
	}

	get promotionV2visible() {
		return (
			['PH', 'CO', 'AR'].includes(this.country) &&
			this.activeRoute?.name?.includes('app.detail') &&
			this.promotionOffer?.package.packageId === 350 &&
			this.promotionOffer?.id === FALLBACK_PROMOTION_OFFER
		);
	}

	// APPLE_QUALTIY_TV_EXP
	isMounted = false;
	mounted() {
		this.isMounted = true;
	}

	appleQualityTvRegularOffersTracked = false;
	appleQualityTvFreeTrialTracked = false;
	appleQualityTvBuyBoxSeen = false;

	get observeAppleQualityTv() {
		return {
			...observableOptions,
			once: true,
			callback: (isVisible: boolean) => this.onVisibilityAppleQualityTvExpRegularChanged(isVisible),
		};
	}

	onVisibilityAppleQualityTvExpRegularChanged(isVisible: boolean) {
		if (isVisible && this.appleQualityTvExp?.active && !this.appleQualityTvBuyBoxSeen) {
			this.appleQualityTvBuyBoxSeen = true;
			this.trackAppleQualityTv();
		}
	}

	trackAppleQualityTv() {
		if (
			!this.appleQualityTvBuyBoxSeen ||
			(this.appleQualityTvFreeTrialTracked && this.appleQualityTvRegularOffersTracked)
		)
			return;

		if (!this.appleQualityTvFreeTrialTracked && (this.isFreeBuyBox || this.appleQualityTvExp.showQualityTv)) {
			AppleQualityTvExp.trackEvent({
				action: 'impression',
				label: 'free_trial',
				property: 'buybox',
			});
			this.appleQualityTvFreeTrialTracked = true;
		}
		if (!this.isFreeBuyBox && !this.appleQualityTvRegularOffersTracked) {
			AppleQualityTvExp.trackEvent({
				action: 'impression',
				label: 'regular_offer',
				property: 'buybox',
			});
			this.appleQualityTvRegularOffersTracked = true;
		}
	}

	// APPLE_QUALTIY_TV_EXP

	//APPLE_BACKDROP_EXP
	appleBackdropImpressionRegularOffersTracked = false;
	appleBackdropImpressionFreeTrialTracked = false;

	impressionCallback() {
		if (this.appleBackdropExpProps?.appleBackdropExpActive && !this.appleBackdropImpressionFreeTrialTracked) {
			AppleBackdropExp.trackEvent({
				action: 'impression',
				label: 'free_trial',
				property: 'buybox',
			});
			this.appleBackdropImpressionFreeTrialTracked = true;
		}

		// APPLE_QUALTIY_TV_EXP

		if (this.appleQualityTvExp.active && !this.appleQualityTvFreeTrialTracked) {
			AppleQualityTvExp.trackEvent({
				action: 'impression',
				label: 'free_trial',
				property: 'buybox',
			});
			this.appleQualityTvFreeTrialTracked = true;
		}
		// APPLE_QUALTIY_TV_EXP
	}

	get appleBackdropExpRegularOptions() {
		return {
			...observableOptions,
			once: true,
			callback: (isVisible: boolean) => this.onVisibilityAppleBackdropExpRegularChanged(isVisible),
		};
	}

	onVisibilityAppleBackdropExpRegularChanged(isVisible: boolean) {
		if (
			isVisible &&
			this.appleBackdropExpProps?.appleBackdropExpActive &&
			!this.appleBackdropImpressionRegularOffersTracked
		) {
			AppleBackdropExp.trackEvent({
				action: 'impression',
				label: 'regular_offer',
				property: 'buybox',
			});
			this.appleBackdropImpressionRegularOffersTracked = true;
		}
	}
	// APPLE_BACKDROP_EXP

	get queryVariables(): GetTitleOffersQueryVariables {
		return {
			nodeId: this.titleId,
			country: this.country,
			language: this.language,
			filterBuy: {
				monetizationTypes: [MonetizationType.Buy],
				bestOnly: true,
				presentationTypes: this.presentationTypeGraphql && [this.presentationTypeGraphql],
			},

			filterFlatrate: {
				monetizationTypes: [
					MonetizationType.Flatrate,
					MonetizationType.FlatrateAndBuy,
					MonetizationType.Ads,
					MonetizationType.Free,
					MonetizationType.Cinema,
				],
				presentationTypes: this.presentationTypeGraphql && [this.presentationTypeGraphql],
				bestOnly: true,
			},
			filterRent: {
				monetizationTypes: [MonetizationType.Rent],
				presentationTypes: this.presentationTypeGraphql && [this.presentationTypeGraphql],
				bestOnly: true,
			},
			filterFree: {
				monetizationTypes: [MonetizationType.Ads, MonetizationType.Free],
				presentationTypes: this.presentationTypeGraphql && [this.presentationTypeGraphql],
				bestOnly: true,
			},
		};
	}

	get presentationTypeGraphql() {
		// @ts-ignore
		const presentationTypeMap: Record<OfferPresentationType, PresentationType> = {
			[OfferPresentationType._4K]: PresentationType['4K'],
			[OfferPresentationType.HD]: PresentationType.Hd,
			[OfferPresentationType.SD]: PresentationType.Sd,
			[OfferPresentationType.DVD]: PresentationType.Dvd,
			[OfferPresentationType.BLURAY]: PresentationType.Bluray,
			[OfferPresentationType.CANVAS]: PresentationType.Canvas,
		};

		return presentationTypeMap[this.buyboxPresentationType];
	}

	get presentationTypes() {
		return {
			[PresentationType.Hd]: {
				text: 'HD',
				name: 'hd',
			},
			[PresentationType['4K']]: {
				text: '4K',
				name: 'hd',
			},
			[PresentationType.Dvd]: {
				text: 'DVD',
				name: 'dvd',
			},
			[PresentationType.Bluray]: {
				text: 'Blu-Ray',
				name: 'bluray',
			},
		};
	}

	get isFreeBuyBox() {
		return this.buyboxPresentationType === OfferPresentationType.FREE;
	}

	/** Excludes Free. The title does not have stream, rent, buy, or any other offers. */
	get hasNoNonFreeOffers() {
		const offerTypes = ['stream', 'rent', 'buy', 'fast', 'cinema'] as const;
		return offerTypes.reduce((acc, curr) => (acc += (this.offers?.[curr] ?? []).length), 0) === 0;
	}

	get hasOnlyCinemaOffers() {
		const cinemaOfferCount = this.offers.cinema.length;
		return cinemaOfferCount > 0 && cinemaOfferCount === this.offers.offerCount;
	}

	get showPromotionRow() {
		return this.title && !this.isPremium && this.isFreeBuyBox && allowedPromotionLanguages.includes(this.language);
	}

	/** Depending on filters "no offers" can mean different things. */
	get hasNoOffersToShow() {
		// this buybox filter is about Monetization, so here specifically we ask
		// if there are no "FREE" offers only
		if (this.isFreeBuyBox) {
			const hasNoFreeOffers = this.offers.free.length === 0;

			// if there is a promotion from a provider that does have an offer,
			// then techincally the promotion counts as an "offer".
			if (this.showPromotionRow && !this.promotionV2visible) {
				return !this.inPromotionCatalogue && hasNoFreeOffers;
			}

			return hasNoFreeOffers;
		}

		return this.hasNoNonFreeOffers || this.hasOnlyCinemaOffers;
	}

	get lastUpdated() {
		return this.offers.maxOfferUpdatedAt;
	}

	get isTitleDetailPage() {
		return this.activeRoute.name?.includes('app.detail');
	}

	get timestampText() {
		const updated = timestamp(
			this.lastUpdated,
			{ year: 'numeric', month: 'long', day: 'numeric' },
			this.country,
			this.language
		);
		return this.$t('WEBAPP_BUYBOX_UPDATED_TIMESTAMP', {
			services_total: this.titleProviderCount,
			date: updated.date,
			time: updated.time,
		});
	}

	get sponsoredRecommendationPackageId() {
		return this.sponsoredAd?.campaign.watchNowOffer.package.packageId;
	}

	get sponsoredRecommendationCohort() {
		return this.sponsoredAd?.holdoutGroup ? '0' : '1';
	}

	get fastOffers() {
		// Exclude Offers that are no longer active.
		return this.offers.fast.filter(el => new Date(el.availableToTime) > new Date());
	}

	get showFastRow() {
		return this.isFreeBuyBox || this.buyboxPresentationType === null;
	}

	get trackingContexts() {
		const titleContext = new SnowplowTitleContextGraphql(
			this.titleObjectId,
			this.titleObjectType,
			this.seasonNumber,
			this.episodeNumber
		);

		return (this.additionalContexts || []).concat([titleContext]);
	}

	clickOut(payload: { buyboxOffer: OfferGraphql; isLeavingIconShown: boolean; offerLocation?: OfferLocation }) {
		let _offer = payload.buyboxOffer;
		const titleObjectId = this.showDetails ? this.showDetails.objectId : this.titleObjectId;
		const titleObjectType = this.showDetails ? this.showDetails.objectType : this.titleObjectType;

		if (payload.buyboxOffer.package.packageId === this.sponsoredRecommendationPackageId) {
			let contexts: SnowplowContext[] = [];
			if (payload.buyboxOffer) {
				const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer);
				contexts = this.trackingContexts;
				contexts.push(clickoutContext);
			}

			TrackingHelper.trackEvent(
				ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
				{
					action: 'buybox_clickout',
					label: `${this.titleId}_${this.sponsoredRecommendationPackageId}`,
					property: 'buybox',
					value: this.sponsoredRecommendationCohort,
				},
				contexts
			);

			this.$backendTracking.trackSraEvent(SraEventAction.BuyboxClickout, this.sponsoredAd, this.titleId);
		}

		// BUYBOX_EXP_4
		if (this.buyboxExp4ValidationPassed) {
			const contexts = [SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer)];
			if (payload.offerLocation === OfferLocation.FREE_TRIAL) {
				buyboxExp4TrackFreeTrialClickout(contexts);
			} else {
				buyboxExp4TrackClickout(contexts);
			}
		}
		// BUYBOX_EXP_4

		// BUYBOX_EXP_5
		if (this.buyboxExp5ValidationPassed) {
			const contexts = [SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer)];
			if (payload.offerLocation === OfferLocation.FREE_TRIAL) {
				buyboxExp5TrackFreeTrialClickout(contexts);
			} else {
				buyboxExp5TrackClickout(contexts);
			}
		}
		// BUYBOX_EXP_5

		trackClickOutGraphql({
			offer: _offer,
			objectType: titleObjectType,
			objectId: titleObjectId,
			seasonNumber: this.seasonNumber,
			episodeNumber: this.episodeNumber,
			offerLocation: payload.offerLocation ?? OfferLocation.REGULAR_BUYBOX,
			additionalContexts: this.additionalContexts,
			contentType: payload?.isLeavingIconShown ? ClickoutContentType.LeavingSoon : undefined,
		});
		// APPLE_BACKDROP_EXP
		if (this.appleBackdropExpProps?.appleBackdropExpActive) {
			payload.offerLocation === OfferLocation.FREE_TRIAL
				? AppleBackdropExp.trackEvent({
						action: 'click',
						label: 'free_trial',
						property: 'buybox',
				  })
				: AppleBackdropExp.trackEvent({
						action: 'click',
						label: 'regular_offer',
						property: 'buybox',
				  });
		}
		// APPLE_BACKDROP_EXP
		// APPLE_QUALITY_TV_EXP
		if (this.appleQualityTvExp.active) {
			payload.offerLocation === OfferLocation.FREE_TRIAL
				? AppleQualityTvExp.trackEvent({
						action: 'click',
						label: 'free_trial',
						property: 'buybox',
				  })
				: AppleQualityTvExp.trackEvent({
						action: 'click',
						label: 'regular_offer',
						property: 'buybox',
				  });
		}
		// APPLE_QUALITY_TV_EXP
	}

	// APPLE_QUALITY_TV_EXP

	get appleQualityTvExp() {
		const variant = this.activeVariantsWithControlGroup[AppleQualityTvExpName];
		const active =
			this.isMounted &&
			!!variant &&
			!this.isLoggedIn() &&
			this.promotionOffer?.id === 'PROMOTION_OFFER' &&
			this.promotionOffer.package.packageId == 350 &&
			!this.isArbitrating &&
			this.$route.name?.includes('app.detail');

		return {
			variant,
			active,
			showQualityTv: active && variant === 'variant_1' && !this.inline,
		};
	}
	// APPLE_QUALITY_TV_EXP
}
